import { bind } from "@react-rxjs/core"
import { useTranslation } from "react-i18next"
import { map, scan } from "rxjs/operators"

import { Modal } from "@/components/Modal"
import {
  ConnectionStatus,
  connectionStatus$,
  initConnection,
  useConnectionStatus,
} from "@/services/connection"

import { Button } from "./Button"

type DisconnectedStatus =
  | ConnectionStatus.DISCONNECTED
  | ConnectionStatus.IDLE_DISCONNECTED
  | ConnectionStatus.OFFLINE_DISCONNECTED

export const DisconnectionOverlayInner = ({
  connectionStatus,
  onReconnect,
}: {
  connectionStatus: DisconnectedStatus
  onReconnect: () => void
}) => {
  const { t } = useTranslation()
  return (
    <Modal shouldShow>
      {connectionStatus === ConnectionStatus.IDLE_DISCONNECTED ? (
        <div className="flex flex-col gap-xl">
          <p className="text-sm-regular">{t("disconnection.inactivity")}</p>
          <Button variant="outline" size="lg" onClick={onReconnect}>
            {t("disconnection.reconnect")}
          </Button>
        </div>
      ) : (
        t(
          connectionStatus === ConnectionStatus.OFFLINE_DISCONNECTED
            ? "disconnection.offline"
            : "disconnection.reconnecting",
        )
      )}
    </Modal>
  )
}

const [useHideOverlay] = bind(
  connectionStatus$.pipe(
    map((connection) => connection === ConnectionStatus.CONNECTED),
    scan(
      (acc, connected) => {
        return {
          connected,
          hasBeenConnnected: acc.hasBeenConnnected || connected,
        }
      },
      { connected: true, hasBeenConnnected: false },
    ),
    map(({ hasBeenConnnected, connected }) => connected || !hasBeenConnnected),
  ),
  true,
)

export const DisconnectionOverlay = () => {
  const connectionStatus = useConnectionStatus()
  return useHideOverlay() ? null : (
    <DisconnectionOverlayInner
      connectionStatus={connectionStatus as DisconnectedStatus}
      onReconnect={initConnection}
    />
  )
}
